import React, { useEffect, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { Alert, AlertIcon, Box, Button, Heading, Spinner, Text, Tooltip, VStack } from '@chakra-ui/react';
import { Mutation, MutationAuthenticateSmsLoginLinkArgs, SmsLoginResult } from '../../generated/graphql';
import { AUTHENTICATE_SMS_LOGIN_LINK, GET_MY_CLIENT } from './queries';
import { clearSmsLinkCredential, getSmsLinkCredential } from './utils/smsLinkCredential';
import { useAuth } from '../../contexts/AuthContext';

/**
 * The browser that opens an SMS sign-in link. It signs in and lands on the page that asked.
 * It never approves another browser (2026-09-11): the page that asked is signed in by
 * typing the code from the same SMS there. A pre-existing session wins and is never swapped.
 */
export function SmsLinkCallback() {
  const apollo = useApolloClient();
  const { refreshAuth } = useAuth();
  const refresh = useRef(refreshAuth);
  refresh.current = refreshAuth;
  const token = useRef(getSmsLinkCredential());
  const currentSession = useRef(!!localStorage.getItem('auth_token'));
  const exchange = useRef<Promise<SmsLoginResult>>();
  const handled = useRef(false);
  const destination = useRef('/');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(!currentSession.current);
  function continueHere() { clearSmsLinkCredential(); window.location.replace(destination.current); }
  useEffect(() => {
    let stopped = false;
    if (!token.current) { setError('This SMS link is missing. Reopen the message or request a new link.'); setBusy(false); return; }
    if (currentSession.current) return;
    if (!exchange.current) exchange.current = apollo.mutate<Pick<Mutation, 'authenticateSmsLoginLink'>, MutationAuthenticateSmsLoginLinkArgs>({ mutation: AUTHENTICATE_SMS_LOGIN_LINK, variables: { token: token.current }, fetchPolicy: 'no-cache' }).then(({ data }) => {
      if (!data?.authenticateSmsLoginLink?.auth?.token) throw new Error('Sign-in did not complete. Request a new SMS link.');
      return data.authenticateSmsLoginLink;
    });
    void exchange.current.then(async result => {
      if (stopped || handled.current) return;
      handled.current = true;
      if (!localStorage.getItem('auth_token')) localStorage.setItem('auth_token', result.auth!.token!);
      const path = result.returnPath || '/';
      const url = new URL(path, window.location.origin);
      destination.current = path.startsWith('/') && !path.startsWith('//') && url.origin === window.location.origin ? path : '/';
      await apollo.clearStore();
      await apollo.query({ query: GET_MY_CLIENT, fetchPolicy: 'network-only' });
      refresh.current();
      continueHere();
    }).catch(err => { if (!stopped) { setError(err instanceof Error ? err.message : 'Sign-in failed. Request a new SMS link.'); setBusy(false); } });
    return () => { stopped = true; };
  }, [apollo]);
  return <Box minH="100dvh" display="flex" alignItems="center" p={6}><VStack align="stretch" spacing={6} maxW="430px" w="full" mx="auto">
    <Heading size="lg">{error ? 'Sign-in needs attention' : currentSession.current ? 'You’re already signed in' : 'Signing you in…'}</Heading>
    {error && <Alert status="error"><AlertIcon />{error}</Alert>}
    {busy ? <Spinner label="Signing you in" alignSelf="center" /> : <>
      {currentSession.current && <Text>Continue with your current account. To use a different account, sign out first and reopen the SMS.</Text>}
      <Tooltip label="Continue with your signed-in account, or return to sign-in to request another link."><Button onClick={() => { clearSmsLinkCredential(); window.location.replace(localStorage.getItem('auth_token') ? destination.current : '/login'); }}>Continue</Button></Tooltip>
    </>}
  </VStack></Box>;
}
